import { db } from './FishingDB';
import { CatchLog, FishingSession, FishingArea } from '../entities/FishingEntities';

// Export data structure
interface ExportData {
  version: string;
  exportDate: number;
  catches: CatchLog[];
  sessions: FishingSession[];
  areas: FishingArea[];
}

// Export all data to JSON
export async function exportToJSON(): Promise<string> {
  try {
    const [catches, sessions, areas] = await Promise.all([
      db.catches.toArray(),
      db.sessions.toArray(),
      db.areas.toArray()
    ]);

    const exportData: ExportData = {
      version: '1.0',
      exportDate: Date.now(),
      catches,
      sessions,
      areas
    };

    return JSON.stringify(exportData, null, 2);
  } catch (error) {
    console.error('Export error:', error);
    throw new Error('Failed to export data');
  }
}

// Download JSON file
export async function downloadJSON() {
  const json = await exportToJSON();
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fishing-log-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Export to CSV
export async function exportToCSV(): Promise<string> {
  const catches = await db.catches.orderBy('timestamp').toArray();
  
  // CSV headers
  const headers = [
    'Datum',
    'Tid',
    'Art',
    'Status',
    'Vikt (kg)',
    'Längd (cm)',
    'Latitude',
    'Longitude',
    'Vattentemp (°C)',
    'Anteckningar'
  ];

  // CSV rows
  const rows = catches.map(c => {
    const date = new Date(c.timestamp);
    return [
      date.toLocaleDateString('sv-SE'),
      date.toLocaleTimeString('sv-SE'),
      c.species,
      c.caught ? 'Landad' : 'Kontakt',
      c.weight || '',
      c.length || '',
      c.lat?.toFixed(6) || '',
      c.lng?.toFixed(6) || '',
      c.waterTemp || '',
      c.notes ? `"${c.notes.replace(/"/g, '""')}"` : ''
    ];
  });

  // Combine headers and rows
  const csv = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csv;
}

// Download CSV file
export async function downloadCSV() {
  const csv = await exportToCSV();
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fishing-log-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Import from JSON
export async function importFromJSON(jsonString: string): Promise<void> {
  try {
    const data: ExportData = JSON.parse(jsonString);

    // Validate data
    if (!data.catches || !data.sessions || !data.areas) {
      throw new Error('Invalid data format');
    }

    // Clear existing data (optional - could ask user)
    await db.catches.clear();
    await db.sessions.clear();
    await db.areas.clear();

    // Import data
    await db.catches.bulkAdd(data.catches);
    await db.sessions.bulkAdd(data.sessions);
    await db.areas.bulkAdd(data.areas);

    console.log('✅ Data imported successfully');
  } catch (error) {
    console.error('Import error:', error);
    throw new Error('Failed to import data');
  }
}

// Handle file upload
export function handleFileUpload(
  file: File,
  onSuccess: () => void,
  onError: (error: string) => void
) {
  const reader = new FileReader();

  reader.onload = async (e) => {
    try {
      const content = e.target?.result as string;
      await importFromJSON(content);
      onSuccess();
    } catch (error) {
      onError('Failed to import file. Please check the file format.');
    }
  };

  reader.onerror = () => {
    onError('Failed to read file.');
  };

  reader.readAsText(file);
}

// Get statistics for export preview
export async function getExportStats() {
  const [catchCount, sessionCount, areaCount] = await Promise.all([
    db.catches.count(),
    db.sessions.count(),
    db.areas.count()
  ]);

  return {
    catches: catchCount,
    sessions: sessionCount,
    areas: areaCount,
    totalSize: 0 // Could calculate approximate size
  };
}
